import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import Swal from "sweetalert2";

import tabs from "./tabs";
import { cleanString } from "./utils";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    user: null,
    loading: false,
    tabs,
    selectedTab: null,
    checkedEntries: [],
    currentPage: 1,
    entriesPerPage: 100,
    maxVisiblePages: 5,
    sortField: null,
    sortDirection: null,
    modalComponent: null,
    modalTitle: "",
    modalProps: null,
    where: {},
  },

  getters: {
    currentTab: (state) => {
      return state.tabs.find((tab) => tab.id === state.selectedTab);
    },
    currentTabHasOptions: (_, getters) => {
      return getters.currentTab.options.length > 0;
    },
    numPages: (state, getters) => {
      return Math.ceil(getters.currentTab.totalLength / state.entriesPerPage);
    },
    firstPage: (state) => {
      return state.currentPage === 1;
    },
    lastPage: (state, getters) => {
      return state.currentPage === getters.numPages || getters.numPages === 0;
    },
    visiblePagesBeforeCurrent: (state) => {
      return Math.floor(state.maxVisiblePages / 2);
    },
    visiblePagesAfterCurrent: (state) => {
      return Math.ceil(state.maxVisiblePages / 2) - 1;
    },
    limitPages: (state, getters) => {
      const defaults = {
        start: 1,
        end: getters.numPages,
      };

      if (getters.numPages <= state.maxVisiblePages) return defaults;

      if (state.currentPage <= getters.visiblePagesBeforeCurrent) {
        return { ...defaults, end: state.maxVisiblePages };
      } else if (
        state.currentPage + getters.visiblePagesAfterCurrent >=
        getters.numPages
      ) {
        return {
          ...defaults,
          start: getters.numPages - state.maxVisiblePages + 1,
        };
      } else {
        return {
          start: state.currentPage - getters.visiblePagesBeforeCurrent,
          end: state.currentPage + getters.visiblePagesAfterCurrent,
        };
      }
    },
    pages: (state, getters) => {
      const range = [];

      for (let i = getters.limitPages.start; i <= getters.limitPages.end; i++) {
        range.push({
          name: i,
          disabled: i === state.currentPage,
        });
      }

      return range;
    },
  },

  mutations: {
    changeUser: (state, user) => {
      state.user = user;
    },
    selectTab: (state, tab) => {
      state.selectedTab = tab;
    },
    changeFilter: (state, payload) => {
      if (
        payload.value === undefined &&
        Object.prototype.hasOwnProperty.call(state.where, payload.name)
      ) {
        Vue.delete(state.where, payload.name);
      } else {
        Vue.set(state.where, payload.name, payload.value);
      }
    },
    resetFilter: (state) => {
      state.where = {};
    },
    changeLimit: (state, limit) => {
      state.entriesPerPage = limit;
    },
    changePage: (state, page) => {
      state.currentPage = page;
    },
    changeSort: (state, column) => {
      if (
        column === null ||
        (state.sortField === column && state.sortDirection === "asc")
      ) {
        state.sortField = null;
        state.sortDirection = null;
        return;
      }

      state.sortDirection =
        state.sortField === column
          ? state.sortDirection
            ? "asc"
            : "desc"
          : "desc";
      state.sortField = column;
    },
    setEntries: (state, entries) => {
      state.selectedTab.entries = entries;
    },
    toggleLoading: (state) => {
      state.loading = !state.loading;
    },
    showModal: (state, payload) => {
      state.modalComponent = payload.component;
      state.modalTitle = payload.title;
      state.modalProps = payload.props;
    },
    hideModal: (state) => {
      state.modalComponent = null;
    },
  },

  actions: {
    selectTab: ({ commit, dispatch }, tab) => {
      commit("selectTab", tab.id);
      commit("resetFilter");
      commit("changePage", 1);
      commit("changeLimit", 50);
      commit("changeSort", null);

      dispatch("requestEntries");
    },
    changeLimit: ({ commit, dispatch }, limit) => {
      commit("changeLimit", limit);
      commit("changePage", 1);

      dispatch("requestEntries");
    },
    gotoPage: ({ commit, dispatch }, page) => {
      commit("changePage", page);

      dispatch("requestEntries");
    },
    sortColumn: ({ commit, dispatch }, column) => {
      commit("changeSort", column);

      dispatch("requestEntries");
    },
    deleteEntry: ({ getters, dispatch }, num) => {
      if (!window.confirm("Seguro de eliminar entrada")) {
        return;
      }

      axios
        .post("server/index.php", {
          action: getters.currentTab.actions.delete,
          num,
        })
        .then(() => {
          dispatch("requestEntries");
        });
    },
    addEntry: ({ commit, getters, dispatch }, form) => {
      for (let [key, value] of Object.entries(form.data)) {
        if (!Array.isArray(value)) {
          continue;
        }

        form.data[key] = value.join();
      }

      if (getters.currentTab.title === "Cursos") {
        form.data.ID_Curso = `${cleanString(form.data.descripcion, 4)}${
          form.data.ID_Location
        }`;
      }

      axios
        .post("server/index.php", {
          action: form.action,
          ...form.data,
        })
        .then(() => {
          Swal.fire({
            icon: "success",
            title: "Registro creado!",
          }).then(() => {
            commit("hideModal");
            dispatch("requestEntries");
          });
        });
    },
    filterEntries: ({ commit, dispatch }, payload) => {
      commit("changeFilter", payload);

      dispatch("requestEntries");
    },
    checkPassword: async ({ state }, title) => {
      const { value: password } = await Swal.fire({
        icon: "warning",
        title,
        input: "password",
        inputLabel: "Contraseña",
        confirmButtonText: "Adelante!",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        inputValidator: (value) => {
          if (!value) {
            return "Introduzca contraseña";
          }
        },
      });

      if (!password) {
        return false;
      } else if (password !== state.user.clave) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Contraseña incorrecta!",
        });
        return false;
      }

      return true;
    },
    getData: ({ state }, action) => {
      return axios
        .get("server/index.php", {
          params: {
            action,
            colegio: state.user.colegio,
            timestamp: Date.now(),
          },
        })
        .then((response) => response.data);
    },
    requestEntries: ({ state, getters, commit }, params) => {
      const tab = getters.currentTab;
      const where = {
        ...state.where,
        colegio: state.user.colegio,
        codigo_colegios: state.user.grupos,
      };

      commit("toggleLoading");

      axios
        .get("server/index.php", {
          params: {
            action: tab.actions.get,
            ...where,
            page: state.currentPage,
            limit: state.entriesPerPage,
            offset: state.entriesPerPage * (state.currentPage - 1),
            order:
              state.sortField || (tab.id === 7 ? "created_at" : "updatedDate"),
            orderDirection: state.sortDirection || "desc",
            ...params,
            timestamp: Date.now(),
          },
        })
        .then((response) => {
          Vue.set(tab, "totalLength", Number(response.data.total));
          Vue.set(tab, "entries", response.data.entries);
          Vue.set(tab, "extras", response.data.extras);

          state.checkedEntries = [];
          commit("toggleLoading");
        });
    },
  },
});
